import React from 'react';
import styled from 'styled-components';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router';
import { Header } from '@app/navigation/header';
import { Body5, Container, ExtraWideBody, H5, Page } from '@ui';
import { BackArrowButton, SkeletonButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import {
  AuditView,
  CatalogView,
  FmdcTenantContext,
  FmdcTenantProvider,
  GateNotice,
  ReviewQueueView,
  RunDetailView,
  RunsView,
  SettingsView,
  TENANT_GATES,
  UnmappedDetailView,
  ValueDetailView,
  fmdcPaths,
} from '@features/fmdc';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const FMDC_HEADING_ID = 'model-data-capture-heading';

// Which view a pathname is on, and what the chrome says about it: the heading, the width, where
// Back goes. Computed from the path rather than kept in state so a deep link and a click agree.
const VIEWS = {
  RUNS: 'runs',
  RUN: 'run',
  REVIEW: 'review',
  VALUE: 'value',
  UNMAPPED: 'unmapped',
  CATALOG: 'catalog',
  AUDIT: 'audit',
  SETTINGS: 'settings',
};

export const viewForPath = (pathname, base = ROUTES.fmdc) => {
  const rest = pathname.startsWith(base) ? pathname.slice(base.length).replace(/^\/+|\/+$/g, '') : '';
  if (!rest) return { view: VIEWS.RUNS };
  const parts = rest.split('/');
  if (parts[0] === 'catalog') return { view: VIEWS.CATALOG };
  if (parts[0] === 'audit') return { view: VIEWS.AUDIT };
  if (parts[0] === 'settings') return { view: VIEWS.SETTINGS };
  if (parts[0] === 'runs' && parts[1]) {
    const runId = parts[1];
    if (parts[2] === 'review') return { view: VIEWS.REVIEW, runId };
    if (parts[2] === 'values' && parts[3]) return { view: VIEWS.VALUE, runId, rowId: parts[3] };
    if (parts[2] === 'unmapped' && parts[3]) return { view: VIEWS.UNMAPPED, runId, unmappedId: parts[3] };
    return { view: VIEWS.RUN, runId };
  }
  return { view: VIEWS.RUNS };
};

const HEADINGS = {
  [VIEWS.RUNS]: 'Model Data Capture',
  [VIEWS.RUN]: (p) => `Run #${p.runId}`,
  [VIEWS.REVIEW]: 'Review queue',
  [VIEWS.VALUE]: 'Value detail',
  [VIEWS.UNMAPPED]: 'Unmapped row',
  [VIEWS.CATALOG]: 'Catalog',
  [VIEWS.AUDIT]: 'Audit trail',
  [VIEWS.SETTINGS]: 'Settings',
};

// Back goes one level up. A detail opened from the queue carries the queue's URL (filters and
// offset included) in location.state so Back lands exactly where the reviewer was.
export const backTargetFor = (placed, base = ROUTES.fmdc) => {
  switch (placed.view) {
    case VIEWS.VALUE:
    case VIEWS.UNMAPPED:
      return { to: fmdcPaths.review(base, placed.runId), label: 'Back to review queue' };
    case VIEWS.REVIEW:
      return { to: fmdcPaths.run(base, placed.runId), label: 'Back to run' };
    case VIEWS.RUN:
    case VIEWS.CATALOG:
    case VIEWS.AUDIT:
    case VIEWS.SETTINGS:
      return { to: base, label: 'Back to runs' };
    default:
      return { to: ROUTES.home, label: 'Back to home' };
  }
};

const RunRoute = ({ Component }) => {
  const { runId, rowId, unmappedId } = useParams();
  return (
    <Component
      key={`${runId}:${rowId || ''}:${unmappedId || ''}`}
      runId={runId}
      rowId={rowId}
      unmappedId={unmappedId}
    />
  );
};

// The page is a consumer, so the Provider sits outside it (the FeedView split).
export const ModelDataCapture = () => (
  <FmdcTenantProvider>
    <ModelDataCapturePage />
  </FmdcTenantProvider>
);

const ModelDataCapturePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { gate, canAdmin, error, reload } = React.useContext(FmdcTenantContext);

  const placed = viewForPath(location.pathname);
  // While the tenant gate holds, the body is the setup form or a notice whatever the URL says, so
  // the chrome follows the body rather than the path: a deep link to a review queue before setup
  // must not read "Review queue" over a setup form, nor offer a Back to a run that renders the
  // same form again.
  const isGated = gate !== TENANT_GATES.READY;
  const gateShowsSettings =
    canAdmin &&
    (gate === TENANT_GATES.NOT_SET_UP ||
      gate === TENANT_GATES.SETTING_UP ||
      (gate === TENANT_GATES.DISABLED && placed.view === VIEWS.SETTINGS));
  const chrome = isGated ? { view: gateShowsSettings ? VIEWS.SETTINGS : VIEWS.RUNS } : placed;
  const heading = typeof HEADINGS[chrome.view] === 'function' ? HEADINGS[chrome.view](chrome) : HEADINGS[chrome.view];
  // Every view here carries a table or a grid, so the page is 95% of the window: ExtraWideBody is
  // the body without the 1640px cap, and maxWidth="none" lifts Page's own 95%-of-body ceiling so
  // the width below is read against the window rather than a capped column. ExtraWideBody also
  // drops Body's ContentWrapper, whose min-height keeps the footer at the bottom on a short view
  // (Loading…, a gate notice), so the Page carries that floor itself — the same value Body uses.
  const pageWidth = '95%';
  const pageMinHeight = 'calc(100vh - 220px)';
  const back = isGated ? { to: ROUTES.home, label: 'Back to home' } : backTargetFor(placed);

  // Move focus to the heading on every sub-view change (a11y), the ScoreSpec rule.
  React.useEffect(() => {
    document.getElementById(FMDC_HEADING_ID)?.focus();
  }, [location.pathname]);

  const onGoBack = () => {
    const backTo = location.state?.backTo;
    navigate(!isGated && backTo ? backTo : back.to);
  };

  // The ways into the supporting views, on the back-arrow bar of the runs list only (the ScoreSpec
  // list's entry points live there for the same reason: they are ways INTO this screen's work).
  const listActions =
    !isGated && placed.view === VIEWS.RUNS
      ? [
          <SkeletonButton key="catalog" onClick={() => navigate(fmdcPaths.catalog(ROUTES.fmdc))}>
            Catalog
          </SkeletonButton>,
          <SkeletonButton key="audit" onClick={() => navigate(fmdcPaths.audit(ROUTES.fmdc))}>
            Audit trail
          </SkeletonButton>,
          <SkeletonButton key="settings" onClick={() => navigate(fmdcPaths.settings(ROUTES.fmdc))}>
            Settings
          </SkeletonButton>,
        ]
      : [];

  const renderGate = () => {
    if (gate === TENANT_GATES.LOADING) {
      return <Body5 isLowContrast>Loading…</Body5>;
    }
    if (gate === TENANT_GATES.ERROR) {
      return (
        <div>
          <Body5>{error}</Body5>
          <Container top={12}>
            <SkeletonButton onClick={reload}>Retry</SkeletonButton>
          </Container>
        </div>
      );
    }
    // Before the first setup, and while it runs, Settings is the only view: admins see the setup
    // form, members a notice. The URL is left alone so a reload after setup lands where it was.
    if (gate === TENANT_GATES.NOT_SET_UP || gate === TENANT_GATES.SETTING_UP) {
      if (canAdmin) return <SettingsView />;
      return (
        <GateNotice
          title={gate === TENANT_GATES.SETTING_UP ? 'Setting up data capture' : 'Data capture is not set up'}
          body={
            gate === TENANT_GATES.SETTING_UP
              ? 'An administrator has started the setup. This page will update when it finishes.'
              : 'An administrator needs to set up data capture for this account before models can be uploaded.'
          }
        />
      );
    }
    if (gate === TENANT_GATES.DISABLED) {
      if (canAdmin && placed.view === VIEWS.SETTINGS) return <SettingsView />;
      return (
        <GateNotice
          title="Data capture is switched off"
          body={
            canAdmin
              ? 'Every page except Settings answers 404 while it is off. Switch it back on in Settings.'
              : 'An administrator has switched data capture off for this account.'
          }
        >
          {canAdmin && (
            <SkeletonButton onClick={() => navigate(fmdcPaths.settings(ROUTES.fmdc))}>Open settings</SkeletonButton>
          )}
        </GateNotice>
      );
    }
    return (
      <Routes>
        <Route index element={<RunsView />} />
        <Route path="runs/:runId" element={<RunRoute Component={RunDetailView} />} />
        <Route path="runs/:runId/review" element={<RunRoute Component={ReviewQueueView} />} />
        <Route path="runs/:runId/values/:rowId" element={<RunRoute Component={ValueDetailView} />} />
        <Route path="runs/:runId/unmapped/:unmappedId" element={<RunRoute Component={UnmappedDetailView} />} />
        <Route path="catalog" element={<CatalogView />} />
        <Route path="audit" element={<AuditView />} />
        <Route path="settings" element={<SettingsView />} />
        <Route path="*" element={<RunsView />} />
      </Routes>
    );
  };

  return (
    <PageWrapper>
      <Header />
      <ExtraWideBody withFooter>
        <Page width={pageWidth} maxWidth="none" minHeight={pageMinHeight}>
          <BackArrowButton onClick={onGoBack} aria-label={back.label} actions={listActions} />
          <Container top={24}>
            <H5 id={FMDC_HEADING_ID} tabIndex={-1}>
              {heading}
            </H5>
            <Container top={24}>{renderGate()}</Container>
          </Container>
        </Page>
      </ExtraWideBody>
    </PageWrapper>
  );
};

export default ModelDataCapture;
